
import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import { TOrderByDirection } from '@th-common/components/material/MatTable/types/table-order-by';
import { IJobExecutionUpdateNotification } from '@th-common/enums/signalr-methods.enum';
import { TCombinedFiltersType } from '@th-common/enums/status/operational-status.enum';
import { IMarkedVideo, TMarkedVideoSearchRequest } from '@th-common/interfaces/marked-video';
import { IPagedResult } from '@th-common/interfaces/paged-result';
import dayjs from 'dayjs';

import { api } from './api';

export interface IMarkedVideoState {
  combinedFilter: TCombinedFiltersType;
  dataListLoading: boolean;
  searchRequest: TMarkedVideoSearchRequest;
  pagination: Omit<IPagedResult<IMarkedVideo>, 'items'>;
  lastTimeUpdated: string;
  markedVideos: IMarkedVideo[];
}

const initialState: IMarkedVideoState = {
  combinedFilter: 'All',
  dataListLoading: false,
  searchRequest: {
    page: 1,
    pageSize: 36,
    searches: [],
    searchAsOneWord: true,
    orderBy: [
      {
        fieldName: 'id',
        ascending: false,
      },
    ],
  },
  pagination: {
    page: 1,
    pageCount: 0,
    totalCount: 0,
  },
  lastTimeUpdated: dayjs().toISOString(),
  markedVideos: [],
};
export const slice = createSlice({
  name: 'markedVideo',
  initialState,
  reducers: {
    reset: () => ({
      ...initialState,
      lastTimeUpdated: dayjs().toISOString(),
    }),
    setDataListLoading: (state, { payload }: { payload: boolean }) => {
      state.dataListLoading = payload;
    },
    search: (state, { payload }) => {
      state.searchRequest = {
        ...state.searchRequest,
        page: 1,
        searches: payload ? [payload] : [],
      };
    },
    setPage: (state, { payload }: { payload: number }) => {
      state.searchRequest.page = payload;
    },
    setOrderBy: (state, { payload: [fieldName, direction] }: { payload: [string, TOrderByDirection] }) => {
      state.searchRequest = {
        ...state.searchRequest,
        orderBy: state.searchRequest.orderBy.length === 0 || direction ? [
          {
            fieldName,
            ascending: direction === 'asc',
          },
        ] : [],
      };
    },
    signalRMarkedVideoUpdateNotification: (state, { payload }: { payload: IJobExecutionUpdateNotification }) => {
      const {
        jobId, percentDone, executionStatus, videoDownloadId,
      } = payload;

      state.markedVideos = state.markedVideos.map((mv): IMarkedVideo => {
        if (mv.id === jobId) {
          return {
            ...mv,
            jobExecutionStatus: executionStatus,
            percentDone,
            downloadId: videoDownloadId,
          };
        }
        return mv;
      });
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<IMarkedVideoState>) => {
    builder
      .addMatcher(api.endpoints.getMarkedVideos.matchFulfilled, (state, { payload }) => {
        const { items, ...pagination } = payload;
        state.markedVideos = items || [];
        state.pagination = pagination;
        state.lastTimeUpdated = dayjs().toISOString();
      });
  },
});

export const { signalRMarkedVideoUpdateNotification } = slice.actions;
export default slice.reducer;
